import axios, {
  AxiosError,
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  InternalAxiosRequestConfig,
} from "axios"
import { camelizeKeys, decamelizeKeys } from "humps"
// The i18next singleton the app initializes, without pulling in that initialization here.
import i18n from "i18next"
import toast from "react-hot-toast"

import {
  createTokens,
  getAccessToken,
  getRefreshToken,
  removeTokens,
} from "@/shared/lib/token/token"
import { addAxiosErrorBreadcrumb } from "@/shared/lib/observability/network"
import { HttpStatusCodeEnum, MP_AUTH_API_PREFIX } from "./axios.constants"

export const SERVER_ERROR_TOAST_ID = "api-server-error"

declare module "axios" {
  interface AxiosRequestConfig {
    preserveResponseKeys?: boolean
    /**
     * Skip the server-error toast on a 500.
     *
     * For requests the user did not ask for. A background availability check that fails should
     * leave the page alone and let its caller decide what to show; telling someone about a
     * request they never made is noise they cannot act on. The error still
     * rejects, still reaches the caller, and is still recorded as a breadcrumb.
     */
    suppressServerErrorAlert?: boolean
  }

  interface InternalAxiosRequestConfig {
    preserveResponseKeys?: boolean
    suppressServerErrorAlert?: boolean
  }
}

interface IFailedRequestQueue {
  onSuccess: (token: string) => void
  onFailure: (error: AxiosError) => void
}

let isRefreshing = false
let failedRequestQueue: IFailedRequestQueue[] = []
let refreshCompletion: Promise<void> | null = null

export const waitForAuthenticationRefresh = () =>
  refreshCompletion ?? Promise.resolve()

/**
 * Puts the bearer on ONE request.
 *
 * Deliberately cannot take `instance.defaults`. A token written there outlives the
 * session: `removeTokens()` clears storage, and axios goes on merging the default
 * header into every later request, so a dead session keeps authenticating. The
 * narrow type is what stops that line being re-added as a plausible one-liner.
 */
export const setAuthorizationHeader = (
  request: AxiosRequestConfig | InternalAxiosRequestConfig,
  token: string
) => {
  // The early return here used to leave without setting anything, so a config that
  // arrived without headers got an empty object and no credential. Unreachable in
  // practice -- axios populates headers before any interceptor sees the config -- but
  // a function whose whole job is to attach a bearer should not have a branch that
  // silently does not.
  if (!request.headers) {
    request.headers = {}
  }
  return ((request.headers as Record<string, string>).Authorization =
    `Bearer ${token}`)
}
/**
 * Refreshes the credential, once, however many requests are waiting on it.
 *
 * Takes no axios instance: what it produces goes to storage, and `onRequest` reads
 * it from there per request. Nothing about the refresh belongs to one instance.
 */
const handleRefreshToken = () => {
  if (refreshCompletion) return refreshCompletion

  isRefreshing = true
  const refreshToken = getRefreshToken()

  refreshCompletion = axios
    .post(
      `${import.meta.env.VITE_BASE_API_URL ?? ""}${MP_AUTH_API_PREFIX}/refresh`,
      null,
      {
        headers: {
          Authorization: `Bearer ${refreshToken}`,
        },
      }
    )
    .then((response) => {
      const { accessToken, refreshToken } = camelizeKeys(response.data) as {
        accessToken: string
        refreshToken: string
      }
      createTokens(accessToken, refreshToken)
      // The access token lives in storage and is read per request by onRequest.
      // It used to be copied onto axiosInstance.defaults here as well, where
      // nothing could reach it: removeTokens() empties storage but not defaults,
      // so every request after a torn-down session still carried the dead token.

      failedRequestQueue.forEach((request) => request.onSuccess(accessToken))
      failedRequestQueue = []
    })
    .catch((error) => {
      failedRequestQueue.forEach((request) => request.onFailure(error))
      failedRequestQueue = []
      removeTokens()
    })
    .finally(() => {
      isRefreshing = false
      refreshCompletion = null
    })

  return refreshCompletion
}

const retryAfterRefresh = (
  axiosInstance: AxiosInstance,
  originalConfig: InternalAxiosRequestConfig
) =>
  new Promise<AxiosResponse>((resolve, reject) => {
    failedRequestQueue.push({
      onSuccess: (accessToken) => {
        setAuthorizationHeader(originalConfig, accessToken)
        resolve(axiosInstance(originalConfig))
      },
      onFailure: (error: AxiosError) => {
        reject(error)
      },
    })
  })

const onRequest = (config: InternalAxiosRequestConfig) => {
  const token = getAccessToken()
  if (token) setAuthorizationHeader(config, token)

  const _config = { ...config, headers: config.headers || {} }
  if (config.params) {
    _config.params = decamelizeKeys(config.params)
  }
  // Skip decamelize for non-plain payloads. humps treats FormData/Blob/File
  // as ordinary objects and walks `Object.keys(...)`, which is empty for
  // these built-ins — the result is a brand-new `{}`, silently destroying
  // the upload body. Pass them through untouched and let axios's adapter
  // handle multipart serialization (and the boundary header).
  if (
    config.data &&
    !(config.data instanceof FormData) &&
    !(typeof Blob !== "undefined" && config.data instanceof Blob) &&
    !(
      typeof URLSearchParams !== "undefined" &&
      config.data instanceof URLSearchParams
    )
  ) {
    _config.data = decamelizeKeys(config.data)
  }

  return _config
}

const onRequestError = (error: AxiosError): Promise<AxiosError> => {
  addAxiosErrorBreadcrumb(error)
  return Promise.reject(error)
}

const onResponse = (response: AxiosResponse): AxiosResponse => {
  // camelize response
  if (
    response.data &&
    response.headers["content-type"] === "application/json" &&
    !response.config.preserveResponseKeys
  ) {
    response.data = camelizeKeys(response.data)
  }

  return response
}

/**
 * The 401 bodies that mean "the credential expired", one per backend. Only these are
 * worth answering with a refresh.
 *
 * task_management says AuthError(AuthExpired). UnprocessableKey used to be in this list
 * and never arrives as a 401 -- that backend maps it to 500 -- so it was doing nothing.
 *
 * mp-auth says AuthError(TokenExpired) for the same condition (`verify_jwt_token` maps
 * an expired signature to it). Its other 401s stay out on purpose: Unauthorized is a
 * refusal, InvalidToken a credential that was never good, InvalidCredentials a login
 * that failed. None of them is something a fresh access token would change.
 */
const EXPIRED_CREDENTIAL_ERRORS: readonly string[] = [
  "AuthError(AuthExpired)",
  "AuthError(TokenExpired)",
]

const requestPath = (config: InternalAxiosRequestConfig) =>
  config.url?.split("?")[0] ?? ""

/**
 * A 401 from POST /refresh means the refresh token is dead. Answering it with another
 * refresh would recurse; the failure path in `handleRefreshToken` -- the one place a
 * dead session is proven -- is what clears it.
 */
const isRefreshRequest = (config: InternalAxiosRequestConfig) =>
  requestPath(config).includes(`${MP_AUTH_API_PREFIX}/refresh`)

/**
 * PATCH /accounts/password in the reset flow is authenticated with the 5-minute
 * password-reset token, which the verification form stores under the access-token key
 * (`createTokens(token)`). A TokenExpired there means the reset window closed and the
 * user needs a new code, not that a session should be refreshed: a refresh would post
 * whatever happens to sit under the refresh-token key, and if that succeeded it would
 * replay the reset with an access token the route does not accept.
 *
 * Exactly that path, which mp-auth serves as PATCH only. /accounts/language is an
 * ordinary account call that must still refresh, and /accounts/password/token is
 * requested without a credential and never reaches this handler.
 */
const isPasswordResetRequest = (config: InternalAxiosRequestConfig) =>
  requestPath(config).endsWith(`${MP_AUTH_API_PREFIX}/accounts/password`)

/** Requests whose 401 is never answered with a refresh, whatever the body says. */
const isExemptFromRefresh = (config: InternalAxiosRequestConfig) =>
  isRefreshRequest(config) || isPasswordResetRequest(config)

const isJsonContentType = (contentType: unknown) =>
  typeof contentType === "string" &&
  /^application\/(?:[\w.-]+\+)?json\b/i.test(contentType)

/**
 * Reads a JSON error body back out of a Blob, in place.
 *
 * A request made with `responseType: "blob"` gets its error body as a Blob as well:
 * axios applies the response type to a 401 exactly as it does to the file. Left as a
 * Blob, `data.error` is undefined, the 401 branch cannot classify it, and a download
 * whose credential expired is never refreshed or retried. Decoding it here, before any
 * branch reads it, means the download refreshes like every other request and its
 * caller receives what a JSON request's caller would.
 *
 * Deliberately status-agnostic: a 400, 404 or 500 from a file request is decoded the
 * same way, so no caller has to know that a download's error body could ever be a Blob.
 *
 * Only when the server says the body is JSON, and only if it parses; otherwise the Blob
 * stays exactly as axios handed it over.
 */
const decodeBlobErrorBody = async (error: AxiosError<unknown>) => {
  const response = error.response
  const body: unknown = response?.data
  if (!response || typeof Blob === "undefined" || !(body instanceof Blob)) {
    return
  }
  if (!isJsonContentType(response.headers["content-type"] ?? body.type)) {
    return
  }
  try {
    response.data = JSON.parse(await body.text())
  } catch {
    // Not the JSON it claimed to be. Leave it as it arrived.
  }
}

const createResponseErrorHandler =
  (axiosInstance: AxiosInstance) =>
  async (
    error: AxiosError<{ error?: string }>
  ): Promise<AxiosError | AxiosResponse> => {
    await decodeBlobErrorBody(error)

    if (error?.response?.status === HttpStatusCodeEnum.Unauthorized) {
      const originalConfig = error.config
      const isRefreshableAuthError =
        !!error.response?.data?.error &&
        EXPIRED_CREDENTIAL_ERRORS.includes(error.response.data.error)

      if (
        isRefreshableAuthError &&
        originalConfig &&
        !isExemptFromRefresh(originalConfig)
      ) {
        // Queue first, then start the refresh if nobody has. A request that receives
        // 401 while another request is already refreshing joins the same queue and is
        // replayed by the same refresh. Removing tokens here would make raw
        // EventSource consumers observe an expired or missing access token.
        const retry = retryAfterRefresh(axiosInstance, originalConfig)
        if (!isRefreshing) void handleRefreshToken()
        return retry
      }

      // Everything else falls through without touching the session, and that is the
      // point. This backend answers EVERY authorization refusal with 401 and the body
      // AuthError(Unauthorized) -- somebody else's job, an admin-only page, a CAT role
      // you do not hold -- so clearing here signed people out for asking about
      // something that was simply not theirs. A credential that is genuinely no good
      // does not arrive here at all: a bad token is 500 AuthError(UnprocessableKey) and
      // a missing header is 400. The one place a dead session is proven is the refresh
      // failing, and that path clears already.
    } else if (
      error?.response?.status === HttpStatusCodeEnum.InternalServerError &&
      !error.config?.suppressServerErrorAlert
    ) {
      // A toast, not alert(): a blocking dialog stopped the page until dismissed, said
      // "API 서버 에러" in Korean whatever the UI language, and arrived on top of the caller's
      // own error toast. The fixed id folds a burst of failures into one message.
      toast.error(i18n.t("common.serverError"), { id: SERVER_ERROR_TOAST_ID })
    }

    addAxiosErrorBreadcrumb(error)
    return Promise.reject(error)
  }

export const setupInterceptors = (
  axiosInstance: AxiosInstance
): AxiosInstance => {
  axiosInstance.interceptors.request.use(onRequest, onRequestError)
  axiosInstance.interceptors.response.use(
    onResponse,
    createResponseErrorHandler(axiosInstance)
  )
  return axiosInstance
}
